/**
 * GENERATED from source/repetition-platform.md and source/repetition-cert.md, verbatim. Do not edit the words here: edit the doc
 * (Coleman's) and regenerate, or the verbatim test fails. Structure (block
 * kinds, chapters, exercises, and each block's depth) is decided in
 * scripts/build-concepts.py; depth.js renders the Platform and
 * Certification versions from this one master lesson. See CLAUDE.md,
 * "Concept modules are verbatim", and .claude/skills/concept-module/SKILL.md.
 */

export const REPETITION = {
  "key": "repetition-without-repetition",
  "title": "Repetition Without Repetition",
  "category": "Concepts",
  "platform": {
    "key": "repetition-without-repetition",
    "sub": "How to Get More Reps Without Just Reciting the Same Solution",
    "duration": "8 min read",
    "chapter": {
      "id": "repetition-without-repetition",
      "title": "Repetition Without Repetition"
    }
  },
  "cert": {
    "key": "repetition-without-repetition-cert",
    "duration": "20–25 min"
  },
  "chapters": [
    {
      "id": "quality",
      "title": "Repetition Is Still Important",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Repetition Is Still Important"
        },
        {
          "kind": "text",
          "md": "“Repetition without repetition” can sound like an argument against repetition.\n\nIt isn’t.\n\nAthletes need repetitions.\n\nUsually, we want a lot of them.\n\nA useful way we think about learning at BAM is:"
        },
        {
          "kind": "bigIdea",
          "md": "Learning dose ≈ Quality of Repetition × Quantity of Repetition"
        },
        {
          "kind": "text",
          "md": "This is not a scientific equation. It is a coaching model.\n\nThe point is simple: if the quality of the reps is great but an athlete barely gets any of them, the learning dose may still be small.\n\nAnd if an athlete gets hundreds of reps but they are low-quality, mindless, or disconnected from what we actually want them to learn, volume alone does not solve the problem.\n\nIdeally, we want both.\n\n**High-quality reps. A lot of them.**\n\nThe challenge is that those two things can compete with each other."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "What Makes a Rep “High Quality”?"
        },
        {
          "kind": "text",
          "md": "Representativeness is a major part of it.\n\nIf we are trying to prepare someone for a game, there is value in preserving information and problems that actually exist in the game.\n\nBut representativeness is not the only thing that matters.\n\nA high-quality repetition might also have:\n\n- enough challenge to create adaptation,\n- enough attention and engagement from the athlete,\n- a clear intention,\n- and a direct relationship to the thing currently limiting the skill.\n\nSometimes the most representative task possible is not automatically the best task possible.\n\nAn athlete may have one particular component of a larger skill holding everything back. Temporarily isolating or overloading that component can sometimes be useful.\n\nSo the goal is not:"
        },
        {
          "kind": "bigIdea",
          "md": "Make everything look as much like the game as possible."
        },
        {
          "kind": "text",
          "md": "The goal is:"
        },
        {
          "kind": "bigIdea",
          "md": "Create the repetitions that best help this athlete improve right now."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Quality Is More Than Representativeness",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Representative practice matters.\n\nBut imagine, theoretically, that every repetition in a session could be perfectly representative of the game.\n\nWould that automatically make the session optimal?\n\nAt BAM, our answer is:\n\n**Not necessarily.**\n\nSometimes one component of a larger skill is holding everything else back.\n\nSometimes we want to overload something beyond what the game naturally provides.\n\nSometimes we want more repetitions than a fully representative environment can realistically create.\n\nSometimes we want to isolate a quality so the athlete can attend to it more directly.\n\nSo we should not turn representativeness into another rigid rule.\n\nA useful question is:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "What does this athlete need from this repetition?",
          "depth": "cert"
        },
        {
          "kind": "scienceNerd",
          "blocks": [
            {
              "kind": "prose",
              "md": "This is one reason **representative learning design**, **specificity**, **variability**, and **contextual interference** should not be collapsed into one idea.\n\nThey answer different questions.\n\n- **Representativeness:** Does practice preserve information and action relationships that matter in competition?\n- **Variability:** How much do movement or practice conditions change?\n- **Contextual interference:** How are different tasks or variations organized across repetitions?\n- **Specificity:** How closely does training match the demands we want to adapt to?\n\nA practice task can score high on one and low on another.\n\nThere is no single slider called “good motor learning.”"
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Solving vs. Reciting"
        },
        {
          "kind": "text",
          "md": "This is the distinction we care about most.\n\nImagine an athlete repeats the exact same action, from the exact same setup, with the exact same information, over and over.\n\nThey may be getting repetition.\n\nBut they may also begin **reciting** a solution.\n\nRepetition without repetition tries to keep the repetition while changing the problem just enough that the athlete must continue **solving**.\n\nThe general task may stay the same.\n\nThe exact solution does not have to."
        },
        {
          "kind": "bigIdea",
          "md": "We want athletes repeatedly solving the same general problem, not simply reciting the same solution."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Where Did “Repetition Without Repetition” Come From?",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "The phrase is most closely associated with Soviet movement scientist **Nikolai Bernstein**.\n\nBernstein was interested in how humans coordinate movement despite having many possible ways to accomplish the same task.\n\nOne of his famous observations involved highly skilled blacksmiths repeatedly striking with a hammer.\n\nThe outcome of the strike could remain highly consistent even though the exact movement used to produce it varied from repetition to repetition.\n\nThat helped inspire the idea of:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "Repetition without repetition.",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "The performer repeatedly solves the same task, but the exact movement solution does not need to be reproduced identically.\n\nThis is an important distinction.\n\nRepetition without repetition is not only something coaches intentionally add to practice.\n\nAt some level, it is already built into human movement.",
          "depth": "cert"
        },
        {
          "kind": "scienceNerd",
          "blocks": [
            {
              "kind": "prose",
              "md": "Bernstein's blacksmith observations became an influential example of the body's many **degrees of freedom**.\n\nDifferent joints and components of the movement could vary while still combining to produce a stable task outcome.\n\nModern motor-control research often distinguishes between variability that interferes with the task and variability that can be tolerated or even used while preserving the outcome.\n\nThis is why movement variability should not automatically be treated as error.\n\nEven experts do not reproduce complex movement in exactly the same way every time.\n\n**Key references:** Bernstein's work on coordination and dexterity; Latash's work on motor abundance; Ranganathan & Newell on behavioral flexibility and repetition without repetition."
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "The Changes Can Be Tiny"
        },
        {
          "kind": "text",
          "md": "Repetition without repetition does not require every rep to look wildly different.\n\nSometimes the variation is extremely small.\n\nIn shooting, it could mean:\n\n- catching with a different hand,\n- using slightly different footwork,\n- receiving a higher or lower pass,\n- deciding whether to use a dribble based on the pass or defender,\n- shooting after different entries,\n- or having the ball dropped in slightly different locations.\n\nFor finishing, it might mean adjusting where you jump based on where a coach places their foot.\n\nIt could be responding to an audio cue.\n\nIt could be using a different release point on a floater.\n\nThe goal is not to add randomness because randomness looks creative.\n\nThe goal is to prevent the athlete from completely switching off and simply reproducing the same answer."
        }
      ]
    },
    {
      "id": "variability",
      "title": "Sometimes the Environment Creates the Variation for You",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Sometimes the Environment Creates the Variation for You"
        },
        {
          "kind": "text",
          "md": "Live defense naturally creates a lot of repetition without repetition.\n\nSo do many small-sided games.\n\nWhy?\n\nBecause another human is changing the problem.\n\nThe defender moves slightly differently.\n\nTheir timing changes.\n\nA teammate changes position.\n\nA gap opens a little earlier or later.\n\nThe affordance landscape changes.\n\nEven when a possession looks similar to the naked eye, the athlete may be solving a slightly different problem."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Movement Variability vs. Practice Variability",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "This distinction matters.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Movement variability",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "The athlete's actual movement changes from repetition to repetition.\n\nThis happens even when the practice task looks blocked and repetitive.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Practice variability",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "The coach deliberately changes something about the practice:\n\n- the pass,\n- footwork,\n- start,\n- cue,\n- defender,\n- timing,\n- constraint,\n- or task.\n\nThese are not the same thing.\n\nA player could perform ten apparently identical pull-ups in a row while still producing slightly different movement solutions each time.\n\nThat means coaches should not assume:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "If I don't externally vary every rep, the athlete is receiving no variability.",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Sometimes the movement itself is already complex enough that we would rather prioritize repetition and volume.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Variability Is Not Automatically Good",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "A common mistake is to learn about variability and conclude:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "More variability = better learning.",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "That is too simple.\n\nVariability can increase problem-solving.\n\nIt can create challenge.\n\nIt can force athletes to repeatedly reconstruct a solution.\n\nIt can broaden the range of situations in which a skill is functional.\n\nBut variability can also become noise.\n\nIt can reduce repetition.\n\nIt can make the task so difficult that the athlete no longer gets useful exposure.\n\nIt can pull attention away from the thing we actually want to improve.\n\nThe goal is not to maximize variability.\n\nThe goal is to use the right amount.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Contextual Interference",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "One research area often brought into this conversation is **contextual interference**.\n\nIn simple terms, blocked practice keeps similar repetitions together.\n\nHigher contextual interference changes tasks or variations more frequently.\n\nA classic finding is that blocked practice can make performance look better during practice, while more interleaved or random practice can sometimes improve delayed retention.\n\nThat creates an important distinction:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "Practice performance is not always the same thing as learning.",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "An athlete looking smooth today does not automatically mean they will retain or transfer the skill better later.\n\nBut there is important nuance.",
          "depth": "cert"
        },
        {
          "kind": "scienceNerd",
          "blocks": [
            {
              "kind": "prose",
              "md": "A 2024 systematic review and meta-analysis of contextual interference included 54 studies.\n\nAcross the overall sample, higher contextual interference showed a moderate benefit for delayed retention.\n\nHowever, the effect was much stronger in controlled laboratory settings.\n\nIn applied field settings using sport skills, the advantage of random practice was close to negligible.\n\nEffects were also negligible and statistically insignificant in younger participants.\n\nSo contextual interference is useful evidence that making practice harder or less predictable can sometimes benefit later learning.\n\nIt is **not** proof that coaches should randomize everything.\n\n**Key reference:** Czyż et al. (2024), High contextual interference improves retention in motor learning: systematic review and meta-analysis, Scientific Reports."
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Short-Term Performance, Retention and Transfer",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "This gives us three different things to think about.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Performance",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "How well does the athlete perform the task right now?",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Retention",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Can they still perform it later after the immediate practice effect has faded?",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Transfer",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Can they use what they learned when the situation changes?\n\nThese are not identical.\n\nPure repetition may sometimes create rapid improvements in immediate performance.\n\nThat can be useful.\n\nBut if the athlete only learns one narrow solution under one narrow set of conditions, that improvement may not generalize as well as we want.\n\nThis is one reason repetition without repetition is useful.\n\nIt can allow us to accumulate exposure while repeatedly asking the athlete to reorganize the solution.",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "game",
      "title": "Example: 2v2 Half-Advantage",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Example: 2v2 Half-Advantage"
        },
        {
          "kind": "text",
          "md": "One BAM example is a 2v2 situation where the ball handler begins with an initial defender on their hip.\n\nA second defender is responsible for helping around the dunker spot.\n\nThe offense is repeatedly working on a similar general problem:\n\n1. Keep the initial defender from disrupting the decision as much as possible.\n2. Make the help defender make a decision.\n3. Take the easiest scoring solution available, usually finishing or dumping the ball off.\n\nThat general problem repeats.\n\nBut the exact repetition changes.\n\nThe initial defender may recover differently.\n\nThe help defender may step over at a different time.\n\nThe offense may perceive the same positioning differently.\n\nThe timing of the final decision may change.\n\nThat is repetition.\n\nBut it is not an identical repetition."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "What If the Game Gets Too Repetitive?",
          "depth": "platform"
        },
        {
          "kind": "text",
          "md": "Small-sided games are not magically variable.\n\nA defense can become predictable.\n\nThe offense can discover one solution and keep using it.\n\nThe start can create the same picture every time.\n\nWhen that happens, coaches can change the problem without abandoning it.\n\nTools we use include:",
          "depth": "platform"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Change the start",
          "depth": "platform"
        },
        {
          "kind": "text",
          "md": "A more dynamic or unpredictable start can create more variation before the key decision occurs.\n\nA static start is more likely to repeatedly create a similar initial problem.",
          "depth": "platform"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Change the defense",
          "depth": "platform"
        },
        {
          "kind": "text",
          "md": "Ask the defender to vary their timing or positioning rather than producing the same solution every rep.",
          "depth": "platform"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Use divergent constraints",
          "depth": "platform"
        },
        {
          "kind": "text",
          "md": "Examples include:\n\n- a bingo card requiring several different solutions,\n- not allowing the same solution twice,\n- or creating a requirement that pushes athletes to solve the problem in multiple ways.",
          "depth": "platform"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Change the timing",
          "depth": "platform"
        },
        {
          "kind": "text",
          "md": "Small changes in when a defender can engage or when an action begins can change what the athlete has to perceive and solve.\n\nThe key is that variability should serve the problem.\n\n**More different is not automatically more useful.**",
          "depth": "platform"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "CASE STUDY: 2v2 Half-Advantage",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "After several repetitions, the offense begins solving the game almost exactly the same way each time.\n\nThe defense is also becoming predictable.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Your task",
          "depth": "cert"
        },
        {
          "kind": "writtenResponse",
          "id": "case1",
          "prompt": "Without changing the general problem we care about, list three ways you could create more repetition without repetition.",
          "hint": "Write your answers before continuing.",
          "discussion": "Tools we might use include:\n**Change the start**\nMake the start more dynamic or less predictable.\n**Change defender behavior**\nAsk the defense to vary the timing of when they step over or how they engage.\n**Add a divergent constraint**\nExamples:\n- a bingo card requiring multiple solutions,\n- the offense cannot solve the problem the same way twice,\n- or another rule that forces exploration of different solutions.\n**Change the timing**\nAlter when the defense can engage or when the action begins.\nAll of those can create new problems while preserving the general target.\nBut now comes the more important question.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "The Harder Question",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "**Which change would increase variability without destroying the problem you actually wanted to train?**\n\nMore variation is not automatically better.\n\nIf our constraint causes the athlete to spend most of the repetition solving something unrelated to the original problem, we have increased variability while potentially decreasing quality.\n\nThat is why constraint design requires judgment.",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "reps",
      "title": "Don't Forget the First Word: Repetition",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Don't Forget the First Word: Repetition"
        },
        {
          "kind": "text",
          "md": "There is another side to this.\n\nSometimes it is easy to create an amazing game or highly representative task that produces very few actual repetitions.\n\nThat matters.\n\nIf the athlete only encounters the problem three times in 20 minutes, we may have created high-quality opportunities but not enough exposure.\n\nThis is where coaching becomes partly a logistical problem.\n\nHow do we get athletes more opportunities to solve the thing we care about?"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Ways We Increase Repetition"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Continuous Games"
        },
        {
          "kind": "text",
          "md": "Reduce resets and dead time so the same general problem appears more often."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Smaller Spaces"
        },
        {
          "kind": "text",
          "md": "When appropriate, shrinking the environment can bring athletes into the relevant interaction more frequently."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Snapshot Games"
        },
        {
          "kind": "text",
          "md": "We do not always need to play through an entire possession.\n\nSometimes we can begin closer to the decision or action we actually want repeated."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "On-Air Repetition"
        },
        {
          "kind": "text",
          "md": "Live defense is not required for every rep.\n\nOn-air work can help build action capacity and accumulate volume.\n\nWe can still introduce small amounts of variability through:\n\n- passes,\n- footwork,\n- entries,\n- cues,\n- release points,\n- or task rules.\n\nThe question becomes:"
        },
        {
          "kind": "bigIdea",
          "md": "How can we accumulate more repetitions without removing the thing that makes the repetition useful?"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Two Coaching Problems",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "When designing repetition without repetition, BAM sees two separate problems.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Problem 1: How do we create useful variation?",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "The athlete needs to keep solving rather than reciting.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Problem 2: How do we get enough repetitions?",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "The athlete needs enough exposure for learning to accumulate.\n\nA coach can be excellent at one and terrible at the other.\n\nYou can create an amazing game with rich information and barely get any reps.\n\nOr you can create enormous volume with almost no problem-solving.\n\nGood practice design tries to solve both.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Sometimes Pure Repetition Is Exactly What We Want",
          "depth": "platform"
        },
        {
          "kind": "text",
          "md": "Repetition without repetition is not a commandment.\n\nThere are times when we intentionally simplify.",
          "depth": "platform"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Early Learning",
          "depth": "platform"
        },
        {
          "kind": "text",
          "md": "When something is new, reducing the number of problems the athlete has to solve can help them get comfortable with the basic action.",
          "depth": "platform"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Confidence",
          "depth": "platform"
        },
        {
          "kind": "text",
          "md": "Sometimes we want the athlete to experience success.\n\nA more predictable, higher-volume block of repetitions can help them feel:\n\n**“I can do this.”**\n\nThen we can widen the problem again.",
          "depth": "platform"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Buy-In",
          "depth": "platform"
        },
        {
          "kind": "text",
          "md": "Some athletes are used to more blocked practice.\n\nSome younger or newer players become frustrated when every repetition is difficult.\n\nSometimes we deliberately sprinkle in easier, more repetitive work so they can see progress and build belief in what they are learning.",
          "depth": "platform"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Volume",
          "depth": "platform"
        },
        {
          "kind": "text",
          "md": "Some complex skills already contain plenty of natural movement variability.\n\nEven repeating the same pull-up or jump shot does not mean the athlete's body is producing an identical movement every time.\n\nThere are situations where we would rather accumulate volume than artificially add more practice variability just because we can.",
          "depth": "platform"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Why We Still Use Pure Repetition",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "We should not become ideological about this.\n\nThere are several reasons BAM may deliberately reduce variability.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Early Learning",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "A player may be trying to learn something new.\n\nIf every repetition contains a completely different problem, they may struggle to organize any functional solution at all.\n\nWe can temporarily collapse the problem.\n\nLet them experience it.\n\nLet them find something functional.\n\nThen widen the problem later.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Confidence and Buy-In",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Imagine we are working on balance shots while moving laterally.\n\nThe athlete finds the action extremely difficult.\n\nThey may be a younger player.\n\nThey may be frustrated.\n\nThey may be used to more blocked training.\n\nWe may deliberately create an easier, more repetitive stretch of practice so they can experience success.\n\nThe message becomes:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "You can do this. Now let's take the next step.",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Sometimes we sprinkle those stretches throughout the session.\n\nSometimes we finish with them.\n\nWe are not necessarily claiming this maximizes long-term transfer.\n\nWe are using repetition strategically to build belief, buy-in, and a more stable base from which to continue learning.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "When the Skill Already Contains Variability",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Sometimes we don't need to manufacture much extra variation.\n\nTake a complex action like repeatedly shooting a jump shot or performing the same pull-up.\n\nEven when the practice setup appears blocked, the athlete is not producing a perfectly identical movement on every attempt.\n\nThere is already movement variability built into the task.\n\nIf the action is complex enough and our priority is volume, there are times when we simply let the athlete repeat.\n\nThis is an important coaching judgment:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "Does this task need more variability, or is the athlete already dealing with enough?",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Think of It as a Slider"
        },
        {
          "kind": "text",
          "md": "We do not need to choose between:\n\n**repetition**\n\nand\n\n**variability.**\n\nInstead, imagine sliders."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Quantity"
        },
        {
          "kind": "text",
          "md": "How many meaningful exposures are we getting?"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Variation"
        },
        {
          "kind": "text",
          "md": "How much does the athlete have to adapt from repetition to repetition?"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Challenge"
        },
        {
          "kind": "text",
          "md": "How difficult is the problem?"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Representativeness"
        },
        {
          "kind": "text",
          "md": "How closely does the information and action resemble what matters in competition?\n\nThose sliders can move.\n\nSometimes we want more stability and more volume.\n\nSometimes we want more variability and more problem-solving.\n\nSometimes we need to make the problem easier.\n\nSometimes we need to make it harder.\n\nGood practice design is not about maxing every slider at once.\n\nIt is about knowing which one to move for the athlete in front of you."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "The Main Takeaway"
        },
        {
          "kind": "text",
          "md": "Repetition matters.\n\nWe should not be afraid of volume.\n\nBut the goal is not always to make the athlete reproduce the same answer over and over.\n\nWhenever possible, look for ways to accumulate repetitions while keeping the athlete involved in solving the problem.\n\nSometimes the variation will come naturally from defenders and teammates.\n\nSometimes you will create it through the start, task, cue, pass, constraint, or environment.\n\nAnd sometimes you will deliberately remove variation and let the athlete repeat.\n\nThe coaching skill is knowing which one they need."
        },
        {
          "kind": "bigIdea",
          "md": "Rack up reps. Keep the reps meaningful. Let the athlete solve."
        },
        {
          "kind": "reflection",
          "md": "Look at one activity you use all the time.\n\nAsk yourself:\n\n**Is the athlete repeatedly solving the problem, or have they started reciting the answer?**\n\nThen ask:\n\n**If I made it more variable, would that actually improve the learning experience, or would I just be adding noise?**"
        }
      ]
    },
    {
      "id": "cases",
      "title": "CASE STUDY: Same Goal, Different Practice",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "CASE STUDY: Same Goal, Different Practice",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Consider the goal of accumulating shooting repetitions.\n\nYou could intentionally vary:\n\n- which hand receives the ball,\n- footwork,\n- pass height,\n- whether a dribble is required,\n- entry into the shot,\n- or where the ball is dropped.\n\nOr you could keep the setup highly repetitive and simply accumulate shots.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Question",
          "depth": "cert"
        },
        {
          "kind": "writtenResponse",
          "id": "case2",
          "prompt": "When might the second option be the better choice?",
          "hint": "Write your answer.",
          "discussion": "Possible reasons include:\n- the player is very early in learning the action,\n- confidence is currently a major limitation,\n- the athlete needs a high volume of exposure,\n- the skill itself already contains substantial movement variability,\n- or additional practice variability would distract from the specific quality being developed.\nThe principle is not:\n**Always vary the repetition.**\nIt is:\n**Know what the repetition is for.**",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "apply",
      "title": "DESIGN ACTIVITY: BUILD A REP SYSTEM",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "DESIGN ACTIVITY: BUILD A REP SYSTEM",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "repsystem",
          "numbered": false,
          "intro": "Choose one activity you currently use.\n\nDo not create a new one.\n\nUse something already in your training.",
          "steps": [
            {
              "id": "problem",
              "q": "Step 1: Define the repeated problem",
              "hint": "What do you actually want the athlete solving or improving?\n\nBe specific.",
              "rows": 4
            },
            {
              "id": "quantity",
              "q": "Step 2: Audit quantity",
              "hint": "How many meaningful exposures does the athlete realistically get?\n\nWhere is time being lost?\n\nCould you increase exposure through:\n\n- continuous play,\n- smaller space,\n- a snapshot start,\n- starting closer to the decision,\n- or an on-air repetition?",
              "rows": 4
            },
            {
              "id": "variation",
              "q": "Step 3: Audit variation",
              "hint": "What changes naturally from repetition to repetition?\n\nWhat stays exactly the same?\n\nDoes the athlete still have to solve anything, or are they reciting?",
              "rows": 4
            },
            {
              "id": "more",
              "q": "Step 4: Decide whether more variability would help",
              "hint": "If yes, choose the smallest useful change.\n\nPossible BAM tools include:\n\n- pass variation,\n- footwork variation,\n- dynamic start,\n- defender timing,\n- cue,\n- divergent constraint,\n- different release point,\n- or a different ball location.\n\nDo not add variability simply because you can.",
              "rows": 4
            },
            {
              "id": "less",
              "q": "Step 5: Decide whether less variability would help",
              "hint": "Would temporarily simplifying the task:\n\n- accelerate early learning,\n- increase confidence,\n- improve buy-in,\n- allow more volume,\n- or help isolate the limiting component?\n\nIf yes, simplify it intentionally.",
              "rows": 4
            },
            {
              "id": "rebuild",
              "q": "Step 6: Rebuild the activity",
              "hint": "Now describe how you would run it.\n\nInclude:\n\n**The repeated problem**\n\n**How many repetitions you want**\n\n**What will vary**\n\n**What will stay stable**\n\n**Why**",
              "rows": 4
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Final Reflection",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Look at the practice you just designed.\n\nAsk:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "Am I maximizing useful repetition, or simply making the drill look more game-like and creative?",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Then ask the opposite:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "Am I accumulating volume, or has the athlete stopped solving anything?",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "The best answer is rarely found at either extreme.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Final Takeaway",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Repetition without repetition is not an argument against repetition.\n\nIt is an argument for understanding what repetition actually means.\n\nHuman movement already contains variability.\n\nPractice can add more.\n\nSometimes that variability helps athletes develop adaptable solutions.\n\nSometimes it simply adds noise.\n\nSometimes blocked repetition helps stabilize an action, create confidence, or accumulate volume.\n\nSometimes it creates an athlete who can recite one answer but struggles when the problem changes.\n\nThe coaching skill is learning to manipulate both sides:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "How many useful repetitions can we create?",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "and",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "How much does the athlete need to solve within each one?",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "The goal is not maximum randomness.\n\nThe goal is better learning.",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "Rack up reps. Keep the reps meaningful. Let the athlete solve.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Suggested References",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "- Bernstein, N. A. — foundational work on coordination, dexterity, and “repetition without repetition.”\n- Ranganathan, R. & Newell, K. M. — Repetition Without Repetition: Challenges in Understanding Behavioral Flexibility in Motor Skill.\n- Czyż, S. H. et al. (2024) — High contextual interference improves retention in motor learning: systematic review and meta-analysis, Scientific Reports.\n- Latash, M. L. — work on motor abundance, coordination, and task-relevant variability.",
          "depth": "cert"
        },
        {
          "kind": "relatedConcepts",
          "items": [
            {
              "key": "affordances",
              "title": "Affordances",
              "blurb": "What Does the Player Actually See?"
            },
            {
              "key": "types-of-variability",
              "title": "Types of Variability",
              "blurb": "Stable enough to perform. Adaptable enough to solve."
            }
          ]
        }
      ]
    }
  ]
};
